/**
 * Runtime configuration of a front that ships as ONE image.
 *
 * Vite bakes `import.meta.env` into the bundle at build time. If the
 * environment specific values lived there, every environment would need its own
 * build. The image is built once instead and runs unchanged on the development
 * server and in production, so those values are read when the container starts:
 * `docker-entrypoint.d/40-app-config.sh` writes the `VITE_*` variables of the
 * container environment into `/config.js`, which `index.html` loads before the
 * bundle and which defines `window.__APP_CONFIG__`.
 *
 * Without a container (vite dev, vite preview, the e2e suite) nothing writes
 * that file: the committed `public/config.js` injects nothing, and the values
 * come from `import.meta.env` as in any Vite app.
 *
 * Read the environment through `runtimeEnv()`, never through
 * `import.meta.env.VITE_X` directly: a direct read is frozen into the bundle and
 * ignores what the container was started with.
 */

/** A bag of configuration values, as `import.meta.env` or `window.__APP_CONFIG__` hold them. */
export type RuntimeEnv = Readonly<Record<string, unknown>>;

declare global {
  interface Window {
    /**
     * The `VITE_*` values the container wrote into `/config.js`. Missing before
     * that script runs, empty outside a container. The type is the contract of
     * the file, not a guarantee: `runtimeEnv()` still checks what it reads.
     */
    __APP_CONFIG__?: Readonly<Record<string, unknown>>;
  }
}

const INJECTED_KEY_PREFIX = 'VITE_';

/**
 * `import.meta.env`, overlaid with the `VITE_*` values the container wrote into
 * `/config.js`.
 *
 * - Both sources are read on EVERY call, never when the module loads: the
 *   container writes its config before the app starts, but tests set the build
 *   environment with `vi.stubEnv` after the module is imported.
 * - Only the own properties of the injected object whose key starts with `VITE_`
 *   and whose value is a string are kept. Anything else is ignored: a stray
 *   number, a nested object or an unrelated key must not reach the app.
 * - An injected empty string wins over the build value. That is the operator's
 *   choice (turning a feature off for one environment), not a missing value.
 * - A missing, `null` or non object `window.__APP_CONFIG__` (the script failed
 *   to load, or the page is served by vite) is ignored: the build values stand.
 * - Nothing is mutated. The result is a new frozen object, so a caller cannot
 *   change the configuration of the whole app by accident.
 * - Without `window` (server side rendering, tests running under node) there is
 *   nothing to inject, and `buildEnv` is returned as is.
 *
 * @param buildEnv the values baked at build time, `import.meta.env` by default.
 */
export function runtimeEnv(buildEnv: RuntimeEnv = import.meta.env): RuntimeEnv {
  if (typeof window === 'undefined') return buildEnv;

  const merged: Record<string, unknown> = { ...buildEnv };
  const injected: unknown = window.__APP_CONFIG__;

  if (typeof injected === 'object' && injected !== null && !Array.isArray(injected)) {
    for (const [key, value] of Object.entries(injected)) {
      if (key.startsWith(INJECTED_KEY_PREFIX) && typeof value === 'string') {
        merged[key] = value;
      }
    }
  }

  return Object.freeze(merged);
}
